import { useEffect, useState } from "react";
import api, { formatApiError, fmtINR } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Copy,
  Trash2,
  Eye,
  ExternalLink,
  X,
  Link2,
  Loader2,
} from "lucide-react";

const CATEGORIES = ["", "figures", "manga", "posters", "apparel", "keychains", "cards", "misc"];
const EXPIRY = [
  { v: 1, l: "1 DAY" },
  { v: 3, l: "3 DAYS" },
  { v: 7, l: "7 DAYS" },
  { v: 30, l: "30 DAYS" },
  { v: 0, l: "NEVER" },
];

const EMPTY = {
  name: "",
  category: "",
  min_price: "",
  max_price: "",
  in_stock_only: true,
  search: "",
  expires_in_days: 7,
};

export default function Catalogs() {
  const [catalogs, setCatalogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/catalogs");
    setCatalogs(data);
  };
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        filters: {
          category: form.category || null,
          min_price: form.min_price === "" ? null : parseFloat(form.min_price),
          max_price: form.max_price === "" ? null : parseFloat(form.max_price),
          in_stock_only: !!form.in_stock_only,
          search: form.search || null,
        },
        expires_in_days: form.expires_in_days === 0 ? null : form.expires_in_days,
      };
      await api.post("/api/catalogs", body);
      toast.success("Catalog created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete catalog "${c.name}"?`)) return;
    await api.delete(`/api/catalogs/${c.id}`);
    toast.success("Deleted");
    load();
  };

  const copy = async (url) => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const publicUrl = (token) => `${window.location.origin}/catalog/${token}`;

  const humanFilter = (f) => {
    const bits = [];
    if (f.category) bits.push(f.category.toUpperCase());
    if (f.min_price != null) bits.push(`≥ ${fmtINR(f.min_price)}`);
    if (f.max_price != null) bits.push(`≤ ${fmtINR(f.max_price)}`);
    if (f.in_stock_only) bits.push("IN STOCK");
    if (f.search) bits.push(`"${f.search}"`);
    return bits.length ? bits.join(" · ") : "ALL PRODUCTS";
  };

  const expired = (c) =>
    c.expires_at && new Date(c.expires_at) < new Date();

  return (
    <div className="p-4 sm:p-6 lg:p-12">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-6">
        <div>
          <div className="font-mono text-xs tracking-[0.3em] text-[color:var(--mecha-red)]">
            // SHARE
          </div>
          <h1 className="font-display text-3xl sm:text-5xl mt-3 leading-none">CATALOGS</h1>
          <div className="text-zinc-500 text-sm mt-2 max-w-lg">
            Generate temp links filtered by category & budget. Share on
            WhatsApp — customers see only in-stock matching items.
          </div>
        </div>
        <button
          data-testid="new-catalog-btn"
          onClick={() => setOpen(true)}
          className="btn-brutal bg-[color:var(--mecha-red)] text-white px-6 h-12 font-bold tracking-widest uppercase text-sm flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Catalog Link
        </button>
      </div>

      {catalogs.length === 0 ? (
        <div className="border border-zinc-800 p-16 text-center">
          <Link2 className="h-10 w-10 mx-auto text-zinc-600 mb-4" />
          <div className="text-zinc-400 mb-4">No catalog links yet</div>
          <button
            onClick={() => setOpen(true)}
            className="btn-brutal bg-[color:var(--mecha-red)] text-white px-6 h-11 text-sm font-mono tracking-widest uppercase"
          >
            Generate your first link
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {catalogs.map((c) => (
            <div
              key={c.id}
              className={`card-brutal p-5 ${expired(c) ? "opacity-50" : "hoverable-card"}`}
              data-testid={`catalog-card-${c.token}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">
                    {new Date(c.created_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                    {c.expires_at && (
                      <span className={expired(c) ? "text-[color:var(--mecha-red)] ml-2" : "text-zinc-500 ml-2"}>
                        · {expired(c) ? "EXPIRED" : `EXPIRES ${new Date(c.expires_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
                      </span>
                    )}
                    {!c.expires_at && <span className="text-[color:var(--neon-cyan)] ml-2">· NEVER EXPIRES</span>}
                  </div>
                  <div className="font-display text-2xl mt-2 leading-tight truncate">
                    {c.name}
                  </div>
                  <div className="font-mono text-[10px] tracking-widest text-zinc-400 mt-2">
                    {humanFilter(c.filters)}
                  </div>
                </div>
                <div className="flex items-center gap-1 font-mono text-xs text-zinc-500 shrink-0">
                  <Eye className="h-3 w-3" />
                  <span data-testid={`views-${c.token}`}>{c.view_count || 0}</span>
                </div>
              </div>

              <div className="mt-4 flex items-stretch border border-zinc-800">
                <div className="flex-1 px-3 py-2.5 font-mono text-[11px] text-[color:var(--neon-cyan)] truncate">
                  {publicUrl(c.token)}
                </div>
                <button
                  data-testid={`copy-${c.token}`}
                  onClick={() => copy(publicUrl(c.token))}
                  className="px-3 border-l border-zinc-800 hover:bg-white hover:text-black"
                  title="Copy link"
                >
                  <Copy className="h-4 w-4" />
                </button>
                <a
                  href={publicUrl(c.token)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 border-l border-zinc-800 hover:bg-white hover:text-black flex items-center"
                  title="Open"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <a
                  data-testid={`wa-share-${c.token}`}
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `Hey! Check out this Swagg Squad catalog — ${c.name}\n${publicUrl(c.token)}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 border-l border-zinc-800 flex items-center font-mono text-[10px] tracking-widest text-white hover:opacity-90"
                  style={{ background: "#25D366" }}
                >
                  WA
                </a>
                <button
                  data-testid={`delete-${c.token}`}
                  onClick={() => remove(c)}
                  className="px-3 border-l border-zinc-800 hover:border-[color:var(--mecha-red)] hover:text-[color:var(--mecha-red)]"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="card-brutal w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-zinc-800">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] text-[color:var(--mecha-red)]">
                  // NEW
                </div>
                <h2 className="font-display text-2xl mt-1">CATALOG LINK</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-zinc-500 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={create} className="p-5 space-y-4">
              <Field label="CATALOG NAME">
                <input
                  data-testid="cat-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Figures under 3000"
                  className={inputCls}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="CATEGORY">
                  <select
                    data-testid="cat-category"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className={inputCls}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c || "any"} value={c}>
                        {c ? c.toUpperCase() : "ANY"}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="EXPIRES IN">
                  <select
                    data-testid="cat-expiry"
                    value={form.expires_in_days}
                    onChange={(e) => setForm({ ...form, expires_in_days: parseInt(e.target.value, 10) })}
                    className={inputCls}
                  >
                    {EXPIRY.map((x) => (
                      <option key={x.v} value={x.v}>
                        {x.l}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="MIN PRICE (INR)">
                  <input
                    data-testid="cat-min"
                    type="number"
                    step="1"
                    value={form.min_price}
                    onChange={(e) => setForm({ ...form, min_price: e.target.value })}
                    placeholder="e.g. 0"
                    className={inputCls}
                  />
                </Field>
                <Field label="MAX PRICE (INR)">
                  <input
                    data-testid="cat-max"
                    type="number"
                    step="1"
                    value={form.max_price}
                    onChange={(e) => setForm({ ...form, max_price: e.target.value })}
                    placeholder="e.g. 3000"
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="SEARCH KEYWORD (OPTIONAL)">
                <input
                  data-testid="cat-search"
                  value={form.search}
                  onChange={(e) => setForm({ ...form, search: e.target.value })}
                  placeholder="e.g. Naruto"
                  className={inputCls}
                />
              </Field>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="cat-instock"
                  type="checkbox"
                  checked={form.in_stock_only}
                  onChange={(e) => setForm({ ...form, in_stock_only: e.target.checked })}
                  className="w-4 h-4 accent-[color:var(--mecha-red)]"
                />
                <span className="text-sm">Show only in-stock products</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex-1 h-11 border border-zinc-800 hover:border-white text-xs font-mono tracking-widest uppercase"
                >
                  Cancel
                </button>
                <button
                  data-testid="cat-save"
                  type="submit"
                  disabled={saving}
                  className="btn-brutal flex-1 h-11 bg-[color:var(--mecha-red)] text-white text-xs font-mono tracking-widest uppercase disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate Link"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls =
  "w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-white text-sm focus:outline-none focus:border-[color:var(--mecha-red)] transition-colors";

function Field({ label, children }) {
  return (
    <div>
      <label className="block font-mono text-[10px] tracking-[0.3em] text-zinc-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}
